import {
  combineLatest,
  distinctUntilChanged,
  map,
  of,
  switchMap,
  type Observable,
} from 'rxjs';
import { spreadMap } from '@ardoq/rxbeach';
import { location$ } from 'router/declarative/location$';
import { isRouteActive } from 'router/declarative/filterRouteActive';
import {
  activeFutureStateIds$,
  FUTURE_STATE_ROUTE,
  type ActiveFutureStateIds,
} from 'transformations/futureStatePage/futureStatePage$';
import { futureStateDraftClaims$ } from 'transformations/futureStatePage/futureStateDraftClaims$';
import {
  draftsForFutureState,
  type FutureStateDraftClaimsState,
} from 'transformations/futureStatePage/futureStateDrafts';
import {
  futureStateClaimsData$,
  futureStateRenderResult$,
  type ClaimsData,
  type RenderResult,
} from 'transformations/futureStatePage/futureStateRenderResult$';
import type { AiControlledCommand } from 'aiControlledCommands/types';
import { buildDraftChangesCommand } from './draftChanges/command';
import { toFutureStateDraftCommandContext } from './draftChanges/context';
import {
  rejectedBatch$,
  type RejectedBatch,
} from './draftChanges/rejectedBatch$';

const toDraftChangesCommands = (
  renderResult: RenderResult,
  claimsData: ClaimsData,
  draftState: FutureStateDraftClaimsState,
  rejected: RejectedBatch | null,
  key: ActiveFutureStateIds
): AiControlledCommand[] => {
  const drafts = draftsForFutureState(draftState, key.futureStateId);
  return [
    buildDraftChangesCommand(
      toFutureStateDraftCommandContext(renderResult, drafts, rejected),
      {
        key,
        drafts,
        persisted: claimsData.kind === 'ready' ? claimsData : null,
      },
      rejected
    ),
  ];
};

const draftChangesCommands$ = combineLatest([
  futureStateRenderResult$,
  futureStateClaimsData$,
  futureStateDraftClaims$,
  rejectedBatch$,
  activeFutureStateIds$,
]).pipe(spreadMap(toDraftChangesCommands));

const isFutureStatePageActive$ = location$.pipe(
  map(location => isRouteActive(location, FUTURE_STATE_ROUTE)),
  distinctUntilChanged()
);

export const availableCommands$: Observable<AiControlledCommand[]> =
  isFutureStatePageActive$.pipe(
    switchMap(isActive => (isActive ? draftChangesCommands$ : of([])))
  );
